import axios from 'axios'
import React, {useId, useMemo, useState} from 'react'
import './ChangePasswordForm.css'

const emptyPasswords = {currentPassword: '', newPassword: '', confirmPassword: ''}
const passwordSymbols = "~`!#$%^&*+=-[]\\';,/{}|\":<>?"

export const passwordRequirements = [
  {key: 'length', label: 'Mínimo 8 caracteres', test: (value) => value.length >= 8},
  {key: 'lowercase', label: 'Contiene una letra minúscula', test: (value) => /[a-z]/.test(value)},
  {key: 'uppercase', label: 'Contiene una letra mayúscula', test: (value) => /[A-Z]/.test(value)},
  {key: 'number', label: 'Contiene un número', test: (value) => /[0-9]/.test(value)},
  {key: 'symbol', label: 'Contiene un símbolo', test: (value) => Array.from(passwordSymbols).some((symbol) => value.includes(symbol))},
]

export const validatePassword = (value) => new TextEncoder().encode(value).length <= 128 && passwordRequirements.every((requirement) => requirement.test(value))

// Empty pairs in edit mode mean that the existing password is preserved.
export const validatePasswordFields = (password, confirmation, required = true) => {
  if (!required && !password && !confirmation) return null
  if (!password || !confirmation) return 'Completa los dos campos de contraseña.'
  if (!validatePassword(password)) return 'La nueva contraseña no cumple con los requisitos de seguridad.'
  if (password !== confirmation) return 'Las contraseñas no coinciden.'
  return null
}

export const passwordPayload = (password, confirmPassword) => password || confirmPassword
  ? {password, confirmPassword}
  : {}

function PasswordSecurityFields({
  newPassword,
  confirmPassword,
  onNewPasswordChange,
  onConfirmPasswordChange,
  required = true,
  inputClassName = 'form-control',
  newPasswordLabel = 'Nueva contraseña',
  confirmPasswordLabel = 'Confirmar nueva contraseña',
}) {
  const fieldId = useId()
  const pairRequired = required || Boolean(newPassword || confirmPassword)
  const requirementStates = passwordRequirements.map((requirement) => ({
    ...requirement,
    valid: requirement.test(newPassword),
  }))
  const securePassword = validatePassword(newPassword)
  const confirmationStarted = confirmPassword.length > 0
  const passwordsMatch = confirmationStarted && newPassword === confirmPassword

  return <>
    <label className='change-password-new'><span>{newPasswordLabel}{!required && ' (opcional)'}</span><input className={`${inputClassName} ${newPassword && !securePassword ? 'is-invalid' : ''} ${securePassword ? 'is-valid' : ''}`} type='password' name='newPassword' autoComplete='new-password' required={pairRequired} value={newPassword} onChange={(event) => onNewPasswordChange(event.target.value)} aria-describedby={`${fieldId}-requirements`} /></label>
    <ul id={`${fieldId}-requirements`} className='change-password-requirements' aria-live='polite'>
      {requirementStates.map((requirement) => <li className={requirement.valid ? 'is-valid' : 'is-pending'} key={requirement.key}><i className={`fas ${requirement.valid ? 'fa-check' : 'fa-circle'}`} aria-hidden='true'></i>{requirement.label}</li>)}
    </ul>
    <label className='change-password-confirm'><span>{confirmPasswordLabel}{!required && ' (opcional)'}</span><input className={`${inputClassName} ${confirmationStarted && !passwordsMatch ? 'is-invalid' : ''} ${passwordsMatch ? 'is-valid' : ''}`} type='password' name='confirmPassword' autoComplete='new-password' required={pairRequired} value={confirmPassword} onChange={(event) => onConfirmPasswordChange(event.target.value)} aria-describedby={confirmationStarted && !passwordsMatch ? `${fieldId}-confirmation` : undefined} /></label>
    {confirmationStarted && !passwordsMatch && <div id={`${fieldId}-confirmation`} className='change-password-confirmation-error'>Las contraseñas no coinciden.</div>}
  </>
}

function ChangePasswordForm({endpoint, requireCurrentPassword = true, submitLabel = 'Actualizar contraseña', successMessage = 'Contraseña actualizada correctamente.', confirmationMessage = null, onSuccess = null, onCancel = null}) {
  const API_URL = process.env.REACT_APP_API_URL
  const [passwords, setPasswords] = useState(emptyPasswords)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState(null)

  const securePassword = useMemo(() => validatePassword(passwords.newPassword), [passwords.newPassword])
  const confirmationStarted = passwords.confirmPassword.length > 0
  const passwordsMatch = confirmationStarted && passwords.newPassword === passwords.confirmPassword
  const canSubmit = (!requireCurrentPassword || Boolean(passwords.currentPassword)) && securePassword && passwordsMatch && !saving

  const updateField = (event) => {
    setPasswords({...passwords, [event.target.name]: event.target.value})
    setMessage(null)
  }

  const submit = async (event) => {
    event.preventDefault()
    setMessage(null)
    if ((requireCurrentPassword && !passwords.currentPassword) || !passwords.newPassword || !passwords.confirmPassword) {
      setMessage({type: 'danger', text: requireCurrentPassword ? 'Completa los tres campos de contraseña.' : 'Completa los dos campos de contraseña.'})
      return
    }
    if (!securePassword) {
      setMessage({type: 'danger', text: 'La nueva contraseña no cumple con los requisitos de seguridad.'})
      return
    }
    if (!passwordsMatch) {
      setMessage({type: 'danger', text: 'Las contraseñas no coinciden.'})
      return
    }
    if (confirmationMessage && !window.confirm(confirmationMessage)) return

    setSaving(true)
    try {
      const payload = requireCurrentPassword ? passwords : {newPassword: passwords.newPassword, confirmPassword: passwords.confirmPassword}
      const {data} = await axios.put(`${API_URL}/${endpoint}`, payload)
      setPasswords(emptyPasswords)
      setMessage({type: 'success', text: successMessage || data.message})
      if (onSuccess) onSuccess(data)
    } catch (error) {
      console.error('No fue posible actualizar la contraseña.', error)
      setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible actualizar la contraseña. Inténtalo nuevamente.'})
    } finally {
      setSaving(false)
    }
  }

  return <form className={`change-password-form ${requireCurrentPassword ? '' : 'change-password-form-reset'}`.trim()} onSubmit={submit} noValidate>
    {requireCurrentPassword && <label className='change-password-current'><span>Contraseña actual</span><input className='form-control' type='password' name='currentPassword' autoComplete='current-password' required value={passwords.currentPassword} onChange={updateField} /></label>}
    <PasswordSecurityFields
      newPassword={passwords.newPassword}
      confirmPassword={passwords.confirmPassword}
      onNewPasswordChange={(value) => updateField({target: {name: 'newPassword', value}})}
      onConfirmPasswordChange={(value) => updateField({target: {name: 'confirmPassword', value}})}
    />
    {message && <div className={`alert alert-${message.type} change-password-message`} role='status'>{message.text}</div>}
    <div className='change-password-actions'><button className='btn btn-primary' type='submit' disabled={!canSubmit}>{saving ? 'Guardando...' : submitLabel}</button>{onCancel && <button className='btn btn-secondary' type='button' disabled={saving} onClick={onCancel}>Cancelar</button>}</div>
  </form>
}

export {ChangePasswordForm, PasswordSecurityFields}
